import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { 
  Heart, Users, Clock, Euro, Shield, Award, MapPin, Phone, Mail,
  CheckCircle, ArrowRight, Upload, Briefcase, Star, Smile, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

export const CandidaturePage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    city: '',
    experience: '',
    services: [],
    availability: [],
    motivation: '',
    cv: null
  });
  
  // État des erreurs de validation
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  
  // Liste des services - SANS "Accompagnement"
  const serviceOptions = [
    { id: 'menage', label: 'Ménage / Entretien' },
    { id: 'jardinage', label: 'Jardinage' },
    { id: 'bricolage', label: 'Bricolage' },
    { id: 'administratif', label: 'Aide administrative' },
  ];
  
  // Jours de la semaine pour les disponibilités
  const daysOfWeek = [
    { id: 'lundi', label: 'Lundi' },
    { id: 'mardi', label: 'Mardi' },
    { id: 'mercredi', label: 'Mercredi' },
    { id: 'jeudi', label: 'Jeudi' },
    { id: 'vendredi', label: 'Vendredi' },
    { id: 'samedi', label: 'Samedi' },
    { id: 'dimanche', label: 'Dimanche' },
  ];
  
  // Validation des champs
  const validateField = (name, value) => {
    switch (name) {
      case 'firstName':
        if (!value.trim()) return 'Le prénom est requis';
        if (value.length < 2) return 'Le prénom doit contenir au moins 2 caractères';
        return '';
      case 'lastName':
        if (!value.trim()) return 'Le nom est requis';
        if (value.length < 2) return 'Le nom doit contenir au moins 2 caractères';
        return '';
      case 'email':
        if (!value.trim()) return 'L\'adresse e-mail est requise';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Veuillez entrer une adresse e-mail valide';
        return '';
      case 'phone':
        if (!value.trim()) return 'Le numéro de téléphone est requis';
        if (!/^(0[1-9])[\s.-]?([0-9]{2}[\s.-]?){4}$/.test(value.replace(/\s/g, ''))) return 'Veuillez entrer un numéro de téléphone valide (ex: 06 12 34 56 78)';
        return '';
      case 'city':
        if (!value.trim()) return 'La ville est requise';
        return '';
      case 'motivation':
        if (!value.trim()) return 'La motivation est requise';
        if (value.length < 20) return 'Veuillez détailler votre motivation (minimum 20 caractères)';
        return '';
      default:
        return '';
    }
  };
  
  // Gestion du blur (perte de focus)
  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  };
  
  // Gestion du changement de valeur
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Revalider si le champ a déjà été touché
    if (touched[name]) {
      const error = validateField(name, value);
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  };
  
  const toggleService = (serviceId) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(serviceId)
        ? prev.services.filter(id => id !== serviceId)
        : [...prev.services, serviceId]
    }));
  };
  
  const toggleAvailability = (dayId) => {
    setFormData(prev => ({
      ...prev,
      availability: prev.availability.includes(dayId)
        ? prev.availability.filter(id => id !== dayId)
        : [...prev.availability, dayId]
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Valider tous les champs
    const newErrors = {};
    const fieldsToValidate = ['firstName', 'lastName', 'email', 'phone', 'city', 'motivation'];
    
    fieldsToValidate.forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) newErrors[field] = error;
    });
    
    // Vérifier qu'au moins un service est sélectionné
    if (formData.services.length === 0) {
      newErrors.services = 'Veuillez sélectionner au moins un service';
    }
    
    // Vérifier que le CV est téléchargé
    if (!formData.cv) {
      newErrors.cv = 'Le CV est obligatoire pour soumettre votre candidature';
    }
    
    setErrors(newErrors);
    setTouched(fieldsToValidate.reduce((acc, field) => ({ ...acc, [field]: true }), {}));
    
    if (Object.keys(newErrors).length > 0) {
      toast.error('Veuillez corriger les erreurs dans le formulaire');
      return;
    }
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      // D'abord, upload le CV
      let cvFilename = null;
      if (formData.cv) {
        const cvFormData = new FormData();
        cvFormData.append('file', formData.cv);
        
        const cvResponse = await fetch(`${API_URL}/api/candidature/upload-cv`, {
          method: 'POST',
          body: cvFormData
        });
        
        if (cvResponse.ok) {
          const cvData = await cvResponse.json();
          cvFilename = cvData.filename;
        }
      }
      
      // Ensuite, envoyer la candidature
      const payload = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        city: formData.city,
        services: formData.services,
        experience: formData.experience || 'non_specifie',
        availability: formData.availability.length > 0 ? formData.availability.join(', ') : 'non_specifie',
        motivation: formData.motivation,
        cv_filename: cvFilename
      };
      
      const response = await fetch(`${API_URL}/api/candidature`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Erreur lors de l\'envoi de la candidature');
      }
      
      setSubmitted(true);
      toast.success('Votre candidature a été envoyée !');
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message || 'Erreur lors de l\'envoi. Veuillez réessayer.');
    }
  };
  
  // Gestion du téléchargement de CV
  const handleCVUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Vérifier la taille (max 5 Mo)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Le fichier est trop volumineux (max 5 Mo)');
        return;
      }
      // Vérifier le type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Format de fichier non supporté. Utilisez PDF, DOC ou DOCX');
        return;
      }
      setFormData(prev => ({ ...prev, cv: file }));
      setErrors(prev => ({ ...prev, cv: '' }));
      toast.success(`CV "${file.name}" téléchargé`);
    }
  };
  
  const advantages = [
    {
      icon: Euro,
      title: 'Revenus attractifs',
      description: 'Tarifs compétitifs et paiement rapide de vos prestations'
    },
    {
      icon: Clock,
      title: 'Liberté totale',
      description: 'Gérez votre emploi du temps comme vous le souhaitez'
    },
    {
      icon: Shield,
      title: 'Missions régulières',
      description: 'Accès à une clientèle fidèle et récurrente'
    },
    {
      icon: Users,
      title: 'Réseau bienveillant',
      description: 'Intégrez une communauté de professionnels solidaires'
    },
    {
      icon: Award,
      title: 'Formation',
      description: 'Aide à la création de votre statut et formations offertes'
    },
    {
      icon: MapPin,
      title: 'Proximité',
      description: 'Missions proches de chez vous, dans l\'Oise (50km autour de Méru)'
    },
  ];
  
  const values = [
    {
      icon: Heart,
      title: 'Autonomie',
      description: 'Vous êtes votre propre patron, gérez vos missions comme vous le souhaitez'
    },
    {
      icon: Star,
      title: 'Excellence',
      description: 'La qualité de service est notre priorité absolue'
    },
    {
      icon: Smile,
      title: 'Flexibilité',
      description: 'Choisissez vos horaires et vos jours de travail'
    },
  ];
  
  // Composant pour afficher les erreurs
  const ErrorMessage = ({ error }) => {
    if (!error) return null;
    return (
      <div className="flex items-center gap-1 mt-1 text-red-500 text-sm">
        <AlertCircle className="h-4 w-4" />
        <span>{error}</span>
      </div>
    );
  };
  
  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F8FAF9] flex items-center justify-center py-20 font-['Poppins']">
        <Card className="max-w-lg mx-4 shadow-xl border-0 rounded-3xl">
          <CardContent className="p-12 text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-[#0A2320] to-[#153254] rounded-2xl flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-[#153254] mb-4">Merci pour votre candidature !</h2>
            <p className="text-gray-600 mb-8">
              Nous avons bien reçu votre dossier et nous l'étudions avec attention. 
              Notre équipe RH vous recontactera sous 48h.
            </p>
            <Button 
              onClick={() => window.location.href = '/'}
              className="bg-[#0A2320] hover:bg-[#0d2e2a] text-white rounded-xl"
            >
              Retour à l'accueil
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-white font-['Poppins']">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0A2320] to-[#153254] py-20 md:py-28 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-20 left-10 w-64 h-64 bg-white/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-20 w-96 h-96 bg-white/5 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white rounded-xl px-5 py-2 mb-6">
              <Briefcase className="h-5 w-5" />
              <span className="font-medium">Devenez partenaire auto-entrepreneur</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Rejoignez une équipe<br />
              <span className="text-[#D4AF37]">en toute indépendance</span>
            </h1>
            
            <p className="text-lg md:text-xl text-white/90 mb-10 max-w-2xl mx-auto">
              Chez The MajordHome, nous collaborons avec des <strong>auto-entrepreneurs</strong> passionnés. 
              Gardez votre autonomie tout en bénéficiant de missions régulières.
            </p>
            
            <Button 
              size="lg" 
              className="bg-[#D4AF37] hover:bg-[#c9a030] text-white font-semibold text-lg px-10 py-6 shadow-lg rounded-xl"
              onClick={() => document.getElementById('form').scrollIntoView({ behavior: 'smooth' })}
            >
              Déposer ma candidature
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
        
        <div className="absolute bottom-0 left-0 w-full">
          <svg viewBox="0 0 1440 120" className="w-full h-auto">
            <path fill="white" d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z"></path>
          </svg>
        </div>
      </section>
      
      {/* Nos valeurs */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-[#153254] mb-4">
            Les avantages de l'indépendance
          </h2>
          <p className="text-xl text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            Travaillez en toute liberté avec le soutien d'une équipe
          </p>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {values.map((value, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow text-center rounded-2xl">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#0A2320] to-[#153254] rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <value.icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#153254] mb-2">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
      
      {/* Avantages */}
      <section className="py-20 bg-[#F8FAF9]">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-[#153254] mb-12">
            Ce que nous vous offrons
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {advantages.map((advantage, index) => (
              <div 
                key={index} 
                className="bg-white rounded-2xl p-6 shadow-md hover:shadow-lg transition-shadow flex items-start gap-4"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-[#0A2320] to-[#153254] rounded-xl flex items-center justify-center flex-shrink-0">
                  <advantage.icon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-[#153254] mb-1">{advantage.title}</h3>
                  <p className="text-gray-600 text-sm">{advantage.description}</p>
                </div>
              </div>
            ))}
          </div>
          
          {/* Info auto-entrepreneur */}
          <div className="max-w-3xl mx-auto mt-12 bg-white border border-gray-100 rounded-2xl p-6 shadow-md">
            <h3 className="font-bold text-[#153254] mb-3 flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-[#0A2320]" />
              Le statut auto-entrepreneur
            </h3>
            <p className="text-gray-600 text-sm leading-relaxed">
              Pas encore auto-entrepreneur ? Pas de problème ! Nous pouvons vous accompagner dans la 
              création de votre statut. C'est simple, rapide et vous permet de travailler en toute légalité.
            </p>
          </div>
        </div>
      </section>
      
      {/* Témoignage */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="border-0 shadow-xl bg-gradient-to-br from-[#153254] to-[#0A2320] text-white overflow-hidden rounded-3xl">
              <CardContent className="p-10 md:p-14 relative">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl"></div>
                
                <div className="relative z-10">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-[#D4AF37]">
                      <img 
                        src="https://images.unsplash.com/photo-1580489944761-15a19d654956?w=150&h=150&fit=crop&crop=face" 
                        alt="Sophie - Auto-entrepreneur"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <p className="font-bold text-lg">Sophie</p>
                      <p className="text-white/70 text-sm">Auto-entrepreneur depuis 2 ans</p>
                    </div>
                  </div>
                  
                  <blockquote className="text-lg md:text-xl italic text-white/90 leading-relaxed">
                    "Après des années en entreprise, j'ai enfin trouvé l'équilibre parfait. 
                    Je gère mon emploi du temps comme je veux, je choisis mes missions, et The MajordHome 
                    m'apporte une clientèle régulière. L'indépendance avec la sécurité des missions !"
                  </blockquote>
                  
                  <div className="flex gap-1 mt-6">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-[#D4AF37] text-[#D4AF37]" />
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      
      {/* Formulaire avec validation */}
      <section id="form" className="py-20 bg-[#F8FAF9]">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-[#153254] mb-4">
            Déposez votre candidature
          </h2>
          <p className="text-xl text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            Rejoignez notre équipe d'auto-entrepreneurs
          </p>
          
          <Card className="max-w-3xl mx-auto border-0 shadow-xl rounded-3xl">
            <CardContent className="p-8 md:p-10">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Nom & Prénom */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Prénom *</label>
                    <Input 
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Votre prénom"
                      className={`rounded-xl ${errors.firstName && touched.firstName ? 'border-red-500 focus:ring-red-500' : ''}`}
                    />
                    {touched.firstName && <ErrorMessage error={errors.firstName} />}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nom *</label>
                    <Input 
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Votre nom"
                      className={`rounded-xl ${errors.lastName && touched.lastName ? 'border-red-500 focus:ring-red-500' : ''}`}
                    />
                    {touched.lastName && <ErrorMessage error={errors.lastName} />}
                  </div>
                </div>
                
                {/* Email & Téléphone */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Adresse e-mail *</label>
                    <Input 
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="votre@email.com"
                      className={`rounded-xl ${errors.email && touched.email ? 'border-red-500 focus:ring-red-500' : ''}`}
                    />
                    {touched.email && <ErrorMessage error={errors.email} />}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone *</label>
                    <Input 
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="06 12 34 56 78"
                      className={`rounded-xl ${errors.phone && touched.phone ? 'border-red-500 focus:ring-red-500' : ''}`}
                    />
                    {touched.phone && <ErrorMessage error={errors.phone} />}
                  </div>
                </div>
                
                {/* Ville & Expérience */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Ville *</label>
                    <Input 
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Votre ville"
                      className={`rounded-xl ${errors.city && touched.city ? 'border-red-500 focus:ring-red-500' : ''}`}
                    />
                    {touched.city && <ErrorMessage error={errors.city} />}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Expérience</label>
                    <select 
                      name="experience"
                      value={formData.experience}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#0A2320] focus:border-transparent"
                    >
                      <option value="">Sélectionnez</option>
                      <option value="0-1">Moins d'1 an</option>
                      <option value="1-3">1 à 3 ans</option>
                      <option value="3-5">3 à 5 ans</option>
                      <option value="5+">Plus de 5 ans</option>
                    </select>
                  </div>
                </div>
                
                {/* Services - SANS "Accompagnement" */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Domaines de compétence *</label>
                  <div className="flex flex-wrap gap-2">
                    {serviceOptions.map(service => (
                      <button
                        key={service.id}
                        type="button"
                        onClick={() => toggleService(service.id)}
                        className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                          formData.services.includes(service.id)
                            ? 'bg-[#0A2320] text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {service.label}
                      </button>
                    ))}
                  </div>
                  {errors.services && <ErrorMessage error={errors.services} />}
                </div>
                
                {/* Disponibilités */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Vos disponibilités *</label>
                  <p className="text-xs text-gray-500 mb-3">Sélectionnez les jours où vous êtes disponible pour travailler</p>
                  <div className="flex flex-wrap gap-2">
                    {daysOfWeek.map(day => (
                      <button
                        key={day.id}
                        type="button"
                        onClick={() => toggleAvailability(day.id)}
                        className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                          formData.availability.includes(day.id)
                            ? 'bg-[#D4AF37] text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                  {formData.availability.length === 0 && touched.availability && (
                    <ErrorMessage error="Veuillez sélectionner au moins un jour de disponibilité" />
                  )}
                </div>
                
                {/* Motivation */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Pourquoi souhaitez-vous nous rejoindre ? *</label>
                  <Textarea 
                    name="motivation"
                    value={formData.motivation}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    rows={4}
                    placeholder="Parlez-nous de vous et de vos motivations..."
                    className={`rounded-xl ${errors.motivation && touched.motivation ? 'border-red-500 focus:ring-red-500' : ''}`}
                  />
                  {touched.motivation && <ErrorMessage error={errors.motivation} />}
                </div>
                
                {/* CV - OBLIGATOIRE */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">CV *</label>
                  <div 
                    className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors cursor-pointer ${
                      errors.cv ? 'border-red-500 bg-red-50' : formData.cv ? 'border-[#0A2320] bg-[#0A2320]/5' : 'border-gray-300 hover:border-[#0A2320]'
                    }`}
                    onClick={() => document.getElementById('cv-upload').click()}
                  >
                    {formData.cv ? (
                      <>
                        <CheckCircle className="h-8 w-8 text-[#0A2320] mx-auto mb-2" />
                        <p className="text-sm text-[#0A2320] font-medium">{formData.cv.name}</p>
                        <p className="text-xs text-gray-500 mt-1">Cliquez pour remplacer</p>
                      </>
                    ) : (
                      <>
                        <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600">Cliquez pour télécharger votre CV</p>
                        <p className="text-xs text-gray-400 mt-1">PDF, DOC ou DOCX (max 5 Mo)</p>
                      </>
                    )}
                    <input 
                      id="cv-upload"
                      type="file" 
                      className="hidden" 
                      accept=".pdf,.doc,.docx"
                      onChange={handleCVUpload}
                    />
                  </div>
                  {errors.cv && <ErrorMessage error={errors.cv} />}
                </div>
                
                <Button 
                  type="submit" 
                  size="lg" 
                  className="w-full bg-[#0A2320] hover:bg-[#0d2e2a] text-white font-semibold text-lg py-6 rounded-xl"
                >
                  Envoyer ma candidature
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
      
      {/* Contact RH - avec mention horaires */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-600 mb-4">Des questions sur le recrutement ?</p>
          <div className="flex flex-wrap justify-center gap-6">
            <div className="text-center">
              <a href="tel:0981543842" className="flex items-center gap-2 text-[#153254] font-semibold hover:text-[#0A2320] transition-colors">
                <Phone className="h-5 w-5" />
                09 81 54 38 42
              </a>
              <p className="text-xs text-gray-500 mt-1">Disponible aux horaires d'ouverture de l'agence</p>
            </div>
            <a href="mailto:tmc60110@gmail.com" className="flex items-center gap-2 text-[#153254] font-semibold hover:text-[#0A2320] transition-colors">
              <Mail className="h-5 w-5" />
              tmc60110@gmail.com
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
